import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoArrowBack,
  IoChevronForward,
  IoBriefcaseOutline,
  IoBusinessOutline,
  IoChatbubble,
  IoLogoApple,
} from 'react-icons/io5';
import { FcGoogle } from 'react-icons/fc';
import ieumLogo from '../../../assets/ieum-logo.png';

const PROVIDER_META = {
  kakao: { label: '카카오', accent: '#FEE500' },
  google: { label: '구글', accent: '#4285F4' },
  apple: { label: '애플', accent: '#000000' },
};

const ProviderBadge = ({ provider }) => {
  if (provider === 'kakao') return <IoChatbubble size={16} color="#000" />;
  if (provider === 'apple') return <IoLogoApple size={16} color="#000" />;
  return <FcGoogle size={16} />;
};

const SocialRoleSelectPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { authUid, email, displayName, provider, phone, verifiedToken } = state;

  useEffect(() => {
    if (!authUid || !provider) {
      navigate('/login', { replace: true });
    }
  }, [authUid, provider, navigate]);

  if (!authUid || !provider) return null;

  const providerMeta = PROVIDER_META[provider] || PROVIDER_META.google;

  const goSignup = (role) => {
    navigate(`/social/signup/${role}`, {
      state: { authUid, email, displayName, provider, phone, verifiedToken },
    });
  };

  const goLinkExisting = () => {
    navigate('/social/link-phone', {
      state: { authUid, email, displayName, provider },
    });
  };

  return (
    <Wrapper>
      <Card>
        <BackBtn onClick={() => navigate('/login')}>
          <IoArrowBack size={16} />
          <span>뒤로가기</span>
        </BackBtn>

        <BrandRow>
          <img src={ieumLogo} alt="이음" />
          <strong>이음 파트너</strong>
        </BrandRow>

        <Title>
          <Accent>{providerMeta.label}</Accent>로 계속하기
        </Title>
        <SubTitle>처음 방문하셨나요? 회원 유형을 선택해 주세요.</SubTitle>

        <Divider />

        <SocialAccountChip>
          <ProviderBadge provider={provider} />
          <span>{providerMeta.label} 계정으로 로그인</span>
        </SocialAccountChip>

        <SectionLabel>회원 유형을 선택해 주세요</SectionLabel>

        <RoleCard onClick={() => goSignup('partner')}>
          <RoleIcon $variant="partner">
            <IoBriefcaseOutline size={20} />
          </RoleIcon>
          <RoleBody>
            <RoleName>제휴 업체</RoleName>
            <RoleDesc>이사, 인테리어, 가전 등 서비스 제공 업체</RoleDesc>
          </RoleBody>
          <IoChevronForward size={18} color="#9CA3AF" />
        </RoleCard>

        <RoleCard onClick={() => goSignup('realtor')}>
          <RoleIcon $variant="realtor">
            <IoBusinessOutline size={20} />
          </RoleIcon>
          <RoleBody>
            <RoleName>공인중개사</RoleName>
            <RoleDesc>부동산 중개 서비스를 제공하는 공인중개사</RoleDesc>
          </RoleBody>
          <IoChevronForward size={18} color="#9CA3AF" />
        </RoleCard>

        <LinkDivider><span>이미 계정이 있으신가요?</span></LinkDivider>

        <LinkBtn type="button" onClick={goLinkExisting}>
          기존 계정으로 연결하기
        </LinkBtn>
      </Card>
    </Wrapper>
  );
};

export default SocialRoleSelectPage;

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.bg};

  @media (min-width: 1024px) {
    align-items: center;
    background: #2f3542;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xl};

  @media (min-width: 1024px) {
    padding: 40px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  }
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};

  img { width: 24px; height: 24px; object-fit: contain; }
  strong {
    font-size: ${({ theme }) => theme.fontSize.base};
    color: ${({ theme }) => theme.colors.text};
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const Accent = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const SubTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
`;

const SocialAccountChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 10px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const RoleCard = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: 18px 18px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: #fff;
  cursor: pointer;
  text-align: left;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  &:hover { border-color: ${({ theme }) => theme.colors.primary}; background: #F9FAFF; }
`;

const RoleIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.radius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary}14;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`;

const RoleBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const RoleName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`;

const RoleDesc = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LinkDivider = styled.div`
  display: flex;
  align-items: center;
  margin: ${({ theme }) => theme.spacing.xl} 0 ${({ theme }) => theme.spacing.base};
  color: ${({ theme }) => theme.colors.textLight};
  font-size: ${({ theme }) => theme.fontSize.sm};

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }

  & > span {
    padding: 0 12px;
  }
`;

const LinkBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: #fff;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;
